import { AppNotification } from './NotificationCenter.types';
import { NOTIFICATION_CATEGORY } from './NotificationCenter.constants';
import { COMMENTS_THREAD_SOURCES } from '../../store/commentsThread/commentsThread_reducer';
import { INVESTOR_ROUTES } from '../../utils/constants/routesConst';
import type { CommentsHeaderLink } from '../../store/commentsThread/commentsThread';
import { COMPANY_REPORT_ORIGIN, INVESTOR_DEALS_PATH } from '../../utils/companyReportNavigation';

interface LeadDrawerPayload {
    source: 'COMPANIES_LIST';
    assesseeOrgId: string;
    headerText?: string;
    headerSecondaryText?: string;
    headerLink?: CommentsHeaderLink;
}

interface DealDrawerPayload {
    source: 'DEAL';
    dealId: string;
    assesseeOrgId?: string;
    headerText?: string;
    headerSecondaryText?: string;
    headerLink?: CommentsHeaderLink;
}

interface InvestorDealDrawerPayload {
    source: 'INVESTOR_DEAL';
    dealIdForInvestorDeal: string;
    dealId: string;
    assesseeOrgId?: string;
    headerText?: string;
    headerSecondaryText?: string;
    headerLink?: CommentsHeaderLink;
}

export type NotificationDrawerPayload = LeadDrawerPayload | DealDrawerPayload | InvestorDealDrawerPayload;

export interface NotificationRoute {
    /** When set, navigate to this path before dispatching the drawer. Omitted for in-place flows (e.g. LEAD). */
    path?: string;
    /** When set, open the comments drawer with this payload. Omitted when notification lacks the ids the drawer needs (e.g. pre-Phase-2 DEAL audits without dealId). */
    drawerPayload?: NotificationDrawerPayload;
}

const LEADS_LANDING_PATH = INVESTOR_ROUTES.APP_SCAN;

/**
 * Deal notifications land on the global Deals listing — where users normally read deal
 * comments. The `category` query param picks the tab: mandate comments belong on the
 * Mandate View, investor-deal comments on the (default) Deal View.
 */
const MANDATE_VIEW_PARAM = 'category=MANDATE_VIEW';
const GLOBAL_DEALS_MANDATE_VIEW_PATH = `${INVESTOR_DEALS_PATH}?${MANDATE_VIEW_PARAM}`;
const GLOBAL_DEALS_DEAL_VIEW_PATH = `${INVESTOR_DEALS_PATH}?category=DEAL_VIEW`;

/** The global Deals listing (`/investor/app/deals`) — the "Deals" tab next to "Leads". */
const isOnGlobalDealsPage = (currentPathname: string | undefined): boolean => !!currentPathname && /\/app\/deals\/?$/.test(currentPathname);

const isOnDealContextPath = (currentPathname: string | undefined): boolean =>
    !!currentPathname && (currentPathname.includes('/company-deals') || isOnGlobalDealsPage(currentPathname));

const isOnDealPageForOrg = (currentPathname: string | undefined, orgId: string): boolean =>
    !!currentPathname && currentPathname.includes('/company-deals') && currentPathname.includes(orgId);

const isMandateViewActive = (currentSearch: string | undefined): boolean => !!currentSearch && currentSearch.includes(MANDATE_VIEW_PARAM);

/**
 * Deal-like notifications open in place when the user is already looking at the right
 * surface: the same org's company-deals page (either tab — it shows both views), or the
 * global Deals page on the tab matching the notification (Mandate View for DEAL, Deal
 * View for INVESTOR_DEAL). On the wrong tab we navigate so the page underneath matches
 * the thread that opens.
 */
const shouldOpenDealDrawerInPlace = (
    currentPathname: string | undefined,
    currentSearch: string | undefined,
    orgId: string,
    wantsMandateView: boolean,
): boolean => {
    if (isOnDealPageForOrg(currentPathname, orgId)) return true;
    if (!isOnGlobalDealsPage(currentPathname)) return false;
    return isMandateViewActive(currentSearch) === wantsMandateView;
};

/**
 * Resolves where to navigate and what drawer payload to dispatch for a notification click.
 * Returns null when the notification lacks the ids needed to route (e.g. an old DEAL audit
 * with empty dealId from before Phase 2 BE rolled out) — callers should still mark-read but
 * skip navigation.
 */
export function resolveNotificationRoute(
    notification: AppNotification,
    currentPathname?: string,
    currentSearch?: string,
): NotificationRoute | null {
    const { category, orgId, dealId, parentDealId, companyName, lenderName } = notification;

    if (!orgId) return null;

    switch (category) {
        case NOTIFICATION_CATEGORY.LEAD: {
            // Smart routing: when the user is already inside a Lead-context page the drawer just
            // overlays in place. When they're on a Deal page, route them out to the leads
            // landing first so the context is consistent with the notification they clicked.
            return {
                path: isOnDealContextPath(currentPathname) ? LEADS_LANDING_PATH : undefined,
                drawerPayload: {
                    source: COMMENTS_THREAD_SOURCES.COMPANIES_LIST,
                    assesseeOrgId: orgId,
                    headerText: companyName,
                    headerLink: { orgId, origin: COMPANY_REPORT_ORIGIN.LEADS },
                },
            };
        }

        case NOTIFICATION_CATEGORY.DEAL: {
            // Mandate comment — belongs on the Mandate View tab of the global Deals page.
            const inPlace = shouldOpenDealDrawerInPlace(currentPathname, currentSearch, orgId, true);
            // Drawer payload only when we have a dealId — pre-Phase-2 audits omit it.
            const drawerPayload = dealId
                ? {
                      source: COMMENTS_THREAD_SOURCES.DEAL,
                      dealId,
                      assesseeOrgId: orgId,
                      headerText: companyName,
                      headerSecondaryText: lenderName,
                      headerLink: { orgId, origin: COMPANY_REPORT_ORIGIN.DEALS },
                  }
                : undefined;
            // In place with nothing to open → no action at all; report unroutable so the
            // caller disables View Thread instead of rendering a dead click.
            if (inPlace && !drawerPayload) return null;
            return { path: inPlace ? undefined : GLOBAL_DEALS_MANDATE_VIEW_PATH, drawerPayload };
        }

        case NOTIFICATION_CATEGORY.INVESTOR_DEAL: {
            // Investor-deal comment — belongs on the (default) Deal View tab.
            const inPlace = shouldOpenDealDrawerInPlace(currentPathname, currentSearch, orgId, false);
            // Drawer requires both ids; without them we can still navigate to the deals page.
            const drawerPayload =
                dealId && parentDealId
                    ? {
                          source: COMMENTS_THREAD_SOURCES.INVESTOR_DEAL,
                          dealId,
                          dealIdForInvestorDeal: parentDealId,
                          assesseeOrgId: orgId,
                          headerText: companyName,
                          headerSecondaryText: lenderName,
                          headerLink: { orgId, origin: COMPANY_REPORT_ORIGIN.DEALS },
                      }
                    : undefined;
            if (inPlace && !drawerPayload) return null;
            return { path: inPlace ? undefined : GLOBAL_DEALS_DEAL_VIEW_PATH, drawerPayload };
        }

        default:
            return null;
    }
}

/**
 * Holds a drawer payload across a route transition.
 *
 * Dispatching setShowCommentsThread in the same tick as navigate() loses the drawer:
 * the Deals page's unmount cleanup dispatches closeCommentsThread(), which both closes
 * whatever just opened AND sets closingCommentsThread=true — and the reducer silently
 * ignores setShowCommentsThread while that flag is up. So when a notification click
 * needs to navigate, the payload is parked here and usePendingNotificationDrawer
 * (mounted next to CommentsThread in InvestorPrivateRoutes) dispatches it only after
 * the route has settled and the close handshake has finished.
 */
let pendingDrawerPayload: NotificationDrawerPayload | null = null;

export function setPendingNotificationDrawer(payload: NotificationDrawerPayload): void {
    pendingDrawerPayload = payload;
}

export function peekPendingNotificationDrawer(): NotificationDrawerPayload | null {
    return pendingDrawerPayload;
}

export function consumePendingNotificationDrawer(): NotificationDrawerPayload | null {
    const payload = pendingDrawerPayload;
    pendingDrawerPayload = null;
    return payload;
}

/**
 * Pathname for which a notification click opened the comments drawer.
 *
 * Topbar closes the drawer whenever its selected nav tab changes — including the
 * tab sync that follows the very navigation a notification click performed
 * (e.g. Leads → Deals), which would close the drawer the instant it opened.
 * Topbar consults this mark and skips that close for the marked pathname.
 * Cleared when the drawer closes (see usePendingNotificationDrawer).
 */
let notificationDrawerPathname: string | null = null;

export function markNotificationDrawerOpened(pathname: string): void {
    notificationDrawerPathname = pathname;
}

export function isNotificationDrawerPathname(pathname: string): boolean {
    return notificationDrawerPathname !== null && notificationDrawerPathname === pathname;
}

export function clearNotificationDrawerMark(): void {
    notificationDrawerPathname = null;
}
